import { type ParsedUrlQuery } from 'querystring';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { SortOrderValues } from '@/types/sort';
import { EntityType } from '@/types/entity';
import { DAMNodeType } from '@/types/tree';
import { useRouter } from 'next/router';

export enum ManageType {
  METADATA = 'metadata',
  VARIANTS = 'variants',
  TAGS = 'tags',
  USERS = 'users',
  WORKFLOWS = 'workflows',
  DESTINATIONS = 'destinations',
}

type BrowseType = DAMNodeType;
type ManageTypeProps = ManageType;
type UiSection = 'browse' | 'trash' | 'manage' | 'uploads' | 'duplicates' | 'asset' | 'home' | 'shared' | 'submit';

interface RouteState {
  pathSlugs?: Array<string> | null;
  currentBrowseType?: BrowseType | null;
  currentBrowseId?: string | null;
  currentManageType?: ManageTypeProps | null;
  uiSection: UiSection;
}

const isValidManageType = (type: string): type is ManageType => {
  return ['metadata', 'variants', 'tags', 'users', 'workflows', 'destinations'].includes(type);
};

const parseRoute = (path: string, _query: ParsedUrlQuery): RouteState => {
  // Default
  if (!path) {
    return { uiSection: 'home', currentBrowseType: DAMNodeType.Folder };
  }

  const cleanPath = path.split('?')[0];
  const segments = cleanPath.split('/').filter(Boolean);

  // Default
  if (segments.length === 0) {
    return { uiSection: 'home', currentBrowseType: DAMNodeType.Folder };
  }

  const [section, ...rest] = segments;

  switch (section) {
    case 'browse':
      if (rest.length > 0) {
        return {
          uiSection: 'browse',
          pathSlugs: rest,
        };
      } else {
        return {
          uiSection: 'home',
          pathSlugs: rest,
        };
      }

    case 'asset':
      return {
        uiSection: 'asset',
        pathSlugs: rest,
      };

    case 'trash':
      return {
        uiSection: 'trash',
        pathSlugs: rest,
      };

    case 'manage':
      return {
        pathSlugs: rest,
        uiSection: 'manage',
        currentManageType: isValidManageType(rest[0]) ? rest[0] : null,
        currentBrowseId: rest[1] ?? null,
      };
    case 'duplicates':
      return {
        uiSection: 'duplicates',
        pathSlugs: rest,
      };
    case 'shared':
      return {
        uiSection: 'shared',
        pathSlugs: rest,
      };

    case 'submit':
      return {
        uiSection: 'submit',
        pathSlugs: rest,
      };

    default:
      return {
        currentManageType: undefined,
        uiSection: 'browse',
        currentBrowseType: DAMNodeType.Folder,
        currentBrowseId: null,
        pathSlugs: rest,
      };
  }
};

export const publicPathPrefixes = ['/terms', '/identifier'];
export const sharePathPrefixes = ['/shares'];
export const submitPathPrefixes = ['/submit'];

export const useCurrentPage = () => {
  const path = useRouter().asPath;

  const { query, push, pathname } = useRouter();
  const { uiSection, currentManageType, pathSlugs } = parseRoute(path, query);

  const isPublicRoute = publicPathPrefixes.some((prefix) => pathname?.startsWith(prefix));
  const isPublicSharePage = sharePathPrefixes.some((prefix) => pathname?.startsWith(prefix));
  const isSubmitPage = submitPathPrefixes.some((prefix) => pathname?.startsWith(prefix));
  const forceLoginRequired = query?.dam_auth === 'required';

  const browseRedirection = async (
    parentPathArray: Array<string> | null,
    page?: number,
    sortValue?: SortOrderValues,
    removeParams: Array<string> = [],
  ) => {
    if (parentPathArray === null) {
      void push('/browse');
      return;
    }

    // eslint-disable-next-line prefer-const
    let parentPath = parentPathArray.join('/');

    const removeParamsFromQuery = (queryParam: ParsedUrlQuery, keysToRemove: Array<string>) => {
      const cleaned = { ...queryParam };
      for (const key of keysToRemove) {
        delete cleaned[key];
      }
      return cleaned;
    };

    const cleanedQuery = removeParamsFromQuery(query, [...removeParams, 'slug']);

    await push({
      pathname: `/browse/${parentPath}`,
      query: {
        ...cleanedQuery,
        pageIndex: page ?? 1,
        pageSize: useTableStore.getState().getTableSettings(EntityType.Asset).pageSize,
        sortValue: sortValue ?? SortOrderValues.CREATED_AT,
        queryString: '',
      },
    });
  };

  return {
    isBrowsePage: uiSection === 'browse',
    onTrashSearch: uiSection === 'home' && query?.filters?.includes('trashed:true'),
    isHomePage: uiSection === 'home',
    isAssetPage: uiSection === 'asset',
    isTrashPage: uiSection === 'trash',
    isManageWelcomePage: uiSection === 'manage' && currentManageType === null,
    isTagsPage: uiSection === 'manage' && currentManageType === 'tags',
    isUsersPage: uiSection === 'manage' && currentManageType === 'users',
    isManagePage: uiSection === 'manage',
    isDuplicatesPage: uiSection === 'duplicates',
    isSharesPage: uiSection === 'shared',
    isAssetManagementSection:
      uiSection === 'browse' ||
      uiSection === 'trash' ||
      uiSection === 'uploads' ||
      uiSection === 'duplicates' ||
      uiSection === 'asset' ||
      uiSection === 'home',
    uiSection: uiSection,
    pathSlugs: pathSlugs,
    isPublicRoute,
    isPublicSharePage,
    isSubmitPage,
    forceLoginRequired,
    browseRedirection,
  };
};
